import React, { useEffect } from 'react';
import { PurchaseOrderRecord } from '@/types/domain/po-management';
import { Button } from '@/components/ui/Button';
import { Database, RefreshCw, X, ShieldCheck } from 'lucide-react';

interface PoSapSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmSync: () => Promise<void>;
  po: PurchaseOrderRecord;
  isSubmitting?: boolean;
}

export const PoSapSyncModal: React.FC<PoSapSyncModalProps> = ({
  isOpen,
  onClose,
  onConfirmSync,
  po,
  isSubmitting = false,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isSubmitting) onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md animate-in fade-in" onClick={() => !isSubmitting && onClose()} />

      <div
        role="dialog"
        aria-modal="true"
        className="relative z-50 w-full max-w-lg rounded-2xl border border-border bg-surface dark:bg-[#0e101f] text-foreground p-6 shadow-2xl space-y-4 animate-in zoom-in-95"
      >
        <div className="flex items-start justify-between border-b border-border pb-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl border bg-primary/10 text-primary border-primary/20">
              <Database className="h-5 w-5" />
            </div>

            <div>
              <h3 className="font-extrabold text-base text-foreground">SAP ME21N BAPI Synchronization</h3>
              <p className="text-xs text-foreground-secondary font-medium mt-0.5">
                {po.poNumber} • <strong className="text-foreground">{po.vendorName}</strong>
              </p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            disabled={isSubmitting}
            onClick={onClose}
            className="h-8 w-8 p-0 rounded-full text-foreground-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-4 text-xs">
          <div className="p-4 rounded-xl border border-border bg-muted/40 space-y-2">
            <span className="text-[10px] font-extrabold text-foreground-muted uppercase block">BAPI Payload Metadata</span>
            <div className="grid grid-cols-2 gap-2 text-foreground font-medium">
              <div>Purchasing Org: <strong className="font-mono text-primary">{po.purchasingOrganization}</strong></div>
              <div>Company Code: <strong className="font-mono text-primary">{po.companyCode}</strong></div>
              <div>Plant Allocation: <strong className="font-mono text-primary">{po.plant}</strong></div>
              <div>Vendor Code: <strong className="font-mono text-primary">{po.vendorCode}</strong></div>
            </div>
          </div>

          <div className="p-3 rounded-xl border border-purple-500/30 bg-purple-500/10 text-purple-900 space-y-1">
            <div className="flex items-center gap-1.5 font-bold">
              <ShieldCheck className="h-4 w-4 text-purple-600" />
              <span>Idempotency & Reconciled BAPI Execution</span>
            </div>
            <p className="text-[11px] text-purple-800 font-medium leading-relaxed">
              Submitting BAPI call <strong className="font-mono">BAPI_PO_CREATE1</strong> with request correlation key <strong className="font-mono">{po.id}</strong>. Safe retry enabled.
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button variant="outline" type="button" disabled={isSubmitting} onClick={onClose} className="rounded-xl font-bold">
              Cancel
            </Button>

            <Button
              type="button"
              disabled={isSubmitting}
              onClick={onConfirmSync}
              className="rounded-xl font-bold text-white bg-primary hover:bg-primary/90 shadow-2xs"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Syncing with SAP ME21N...
                </>
              ) : (
                <>
                  <Database className="h-4 w-4 mr-1.5" /> Execute SAP BAPI Sync →
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
